import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { take } from 'rxjs';
import { WaitingListComponent } from './waiting-list/waiting-list.component';
import { CompletedListComponent } from './completed-list/completed-list.component';
import { LivePickupStore } from '@live-pickup/data-access';
import { AuthStore } from '@auth/data-access';

@Component({
  selector: 'lib-live-pickup-view',
  standalone: true,
  imports: [WaitingListComponent, CompletedListComponent],
  templateUrl: './live-pickup-view.component.html',
  styleUrl: './live-pickup-view.component.scss',
})
export class LivePickupViewComponent implements OnInit, OnDestroy {
  readonly store = inject(LivePickupStore);
  private readonly route = inject(ActivatedRoute);
  private readonly authStore = inject(AuthStore);

  ngOnInit(): void {
    this.route.queryParamMap.pipe(take(1)).subscribe((params) => {
      const tenantId = this.authStore.authData()?.TenantId;
      if (!tenantId) return;
      const accessPointId = params.get('accessPointId') ?? undefined;
      const accessPointName = params.get('accessPointName') ?? undefined;
      this.store.connect({ tenantId, accessPointId, accessPointName });
      this.store.loadAccessPoints();
      this.store.loadOpenPickups(accessPointId ?? null);
      this.store.loadRecentCompleted(accessPointId ?? null);
      this.store.loadLatestHero();
    });
    document.addEventListener('visibilitychange', this.handleResume);
    window.addEventListener('pageshow', this.handleResume);
  }

  ngOnDestroy(): void {
    document.removeEventListener('visibilitychange', this.handleResume);
    window.removeEventListener('pageshow', this.handleResume);
    this.store.disconnect();
  }

  /**
   * On a TV the browser freezes while the panel is off; SignalR reconnects on
   * resume but never replays events missed during the freeze, so re-fetch the
   * snapshots to recover the current state (the live stream heals on its own).
   */
  private readonly handleResume = (): void => {
    if (document.visibilityState !== 'visible') return;
    const accessPointId = this.store.accessPointId();
    this.store.loadAccessPoints();
    this.store.loadOpenPickups(accessPointId);
    this.store.loadRecentCompleted(accessPointId);
    this.store.loadLatestHero();
  };

  retryWaiting(): void {
    this.store.loadOpenPickups(this.store.accessPointId());
  }

  retryCompleted(): void {
    this.store.loadRecentCompleted(this.store.accessPointId());
  }
}
